import { Injectable } from '@angular/core';
import { UserService } from './user.service';
import { getActiveAdGroups } from '../../modules/login/util/constants';

/**
 * Entra SSO: AD-group-based nav config service.
 *
 * Mirrors ConfigService.updateConfig() but drives menu/submenu visibility
 * purely from the adGroupAccessIds defined on each item in nav-bar.config.ts,
 * following the same iteration pattern as ConfigService.
 *
 * Environment handling is done via getActiveAdGroups() (login/util/constants.ts),
 * which appends the per-environment suffix (APP_CONFIG.AD_GROUP_SUFFIX, e.g. '-opt',
 * '-qa1', '-qa2', or '' for prod) to each AD group name at lazy-load time.
 * This service simply checks the user's AD groups against those resolved values.
 *
 * No hardcoded menu lists — all access is driven by adGroupAccessIds
 * on each menu, customerAssistanceTool, and adminTool in nav-bar.config.ts.
 */

@Injectable({ providedIn: 'root' })
export class EntraConfigService {

  configData: any;

  constructor(private user: UserService) { }

  private hasBenefitLookupOnlyAccess(): boolean {
    const benefitLookupRepGroup = getActiveAdGroups().BENEFIT_LOOKUP_REP;
    return this.user.getAdGroups.length === 1 && this.user.getAdGroups.includes(benefitLookupRepGroup);
  }

  /**
   * Checks whether the user holds any AD group listed in the item's
   * adGroupAccessIds array (same concept as ConfigService.isAccessible
   * checking accessIds against roles/privileges).
   */
  private isAdGroupAccessible(adGroupAccessIds?: string[]): boolean {
    if (adGroupAccessIds === undefined || adGroupAccessIds === null) return true;
    if (adGroupAccessIds.length === 0) return false;
    return adGroupAccessIds.some(g => this.user.getAdGroups.includes(g));
  }

  /**
   * Updates navMenu visibility based on user's AD groups.
   * Iterates the config in the same way as ConfigService.updateConfig(),
   * but checks adGroupAccessIds on each item instead of accessIds.
   */
  updateConfig(configLinks: any) {
    const benefitLookupOnlyAccess = this.hasBenefitLookupOnlyAccess();

    // Deep-clone to avoid mutating the original config objects on repeated calls
    this.configData = structuredClone(configLinks).map((el: any) => {

      if (benefitLookupOnlyAccess) {
        el.isActive = el.menuId === 'benefits';

        if (Object.hasOwn(el, 'customerAssistanceTools')) {
          el.customerAssistanceTools = el.customerAssistanceTools.filter((cat: any) => cat.subMenuId !== 'none');
          el.customerAssistanceTools.map((cat: any) => {
            cat.hasAccess = el.menuId === 'benefits' && cat.subMenuId === 'benefitLookup';
          });
        }

        if (Object.hasOwn(el, 'adminTools')) {
          el.adminTools.map((at: any) => {
            at.hasAccess = false;
          });
        }

        return el;
      }

      // Top-level menu visibility
      if (Object.hasOwn(el, 'isActive')) {
        el.isActive = this.isAdGroupAccessible(el.adGroupAccessIds);
      }

      // Customer Assistance Tools (sub-menu items)
      if (Object.hasOwn(el, 'customerAssistanceTools')) {
        el.customerAssistanceTools.map((cat: any) => {
          cat.hasAccess = this.isAdGroupAccessible(cat.adGroupAccessIds);
          // AD flow: point My Profile directly to the details page
          if (cat.subMenuId === 'myProfile') {
            cat.subMenuLink = '/my-profile/details';
          }
        });
      }

      // Admin Tools (sub-menu items)
      if (Object.hasOwn(el, 'adminTools')) {
        el.adminTools.map((at: any) => {
          at.hasAccess = this.isAdGroupAccessible(at.adGroupAccessIds);
          // AD flow: point CSA User Report directly to the csa-reports form
          if (at.subMenuId === 'csaUserReport') {
            at.subMenuLink = '/manage-csa-users/reports/csa-reports';
          }
        });
      }

      return el;
    });
  }
}
